import type { ChatMessage, ChatMessageJump, SessionTabItem } from "@ext/agent/components/types/chat";
import type { StoredAttachment } from "@ext/agent/core/attachmentStore";
import type { AgentQuote } from "@ext/agent/core/events";
import type { Dispatch, SetStateAction } from "react";
import { create } from "zustand";
import { useShallow } from "zustand/react/shallow";

interface ChatStoreState {
	sessions: SessionTabItem[];
	activeSessionId: string | null;
	sessionError: string | null;

	messages: ChatMessage[];
	messagesSessionId: string | null;
	streamingMessageId: string | null;
	streamText: string;
	showAgentThinking: boolean;

	draft: string;
	attachments: StoredAttachment[];
	quote: AgentQuote | null;
	sessionLoading: boolean;
	pendingJump: ChatMessageJump | null;
	hydrating: boolean;
	isSending: boolean;
	catalogName: string | null;
	selectedSkillName: string | null;
	browserAllowed: boolean;

	onSelectSession: (id: string) => void;
	onCloseTab: (id: string) => void;
	onRenameSession: (id: string, title: string) => void;
	onNewSession: () => void;
	onDraftChange: (value: string) => void;
	onAttachmentsChange: Dispatch<SetStateAction<StoredAttachment[]>>;
	onSubmit: () => void;
	onCancel: (() => void) | null;
	onSkillChange: (name: string | null) => void;
	onBrowserAllowedChange: (allowed: boolean) => void;

	set: (patch: Partial<Omit<ChatStoreState, "set">>) => void;
}

const useChatStore = create<ChatStoreState>()((set) => ({
	sessions: [],
	activeSessionId: null,
	sessionError: null,

	messages: [],
	messagesSessionId: null,
	streamingMessageId: null,
	streamText: "",
	showAgentThinking: false,

	draft: "",
	attachments: [],
	quote: null,
	sessionLoading: false,
	pendingJump: null,
	hydrating: false,
	isSending: false,
	catalogName: null,
	selectedSkillName: null,
	browserAllowed: false,

	onSelectSession: () => {},
	onCloseTab: () => {},
	onRenameSession: () => {},
	onNewSession: () => {},
	onDraftChange: () => {},
	onAttachmentsChange: () => {},
	onSubmit: () => {},
	onCancel: null,
	onSkillChange: () => {},
	onBrowserAllowedChange: () => {},

	set: (patch) => set(patch),
}));

export const useChatSessions = () =>
	useChatStore(
		useShallow((s) => ({ sessions: s.sessions, activeSessionId: s.activeSessionId, sessionError: s.sessionError })),
	);

export const useChatMessages = () =>
	useChatStore(
		useShallow((s) => ({
			messages: s.messages,
			messagesSessionId: s.messagesSessionId,
			streamingMessageId: s.streamingMessageId,
			showAgentThinking: s.showAgentThinking,
		})),
	);

export const useChatStreamText = (active: boolean) => useChatStore((s) => (active ? s.streamText : ""));

export const useChatDraft = () => useChatStore(useShallow((s) => ({ draft: s.draft, onDraftChange: s.onDraftChange })));

export const useChatQuote = () => useChatStore((s) => s.quote);

export const useChatInput = () =>
	useChatStore(
		useShallow((s) => ({
			attachments: s.attachments,
			isSending: s.isSending,
			catalogName: s.catalogName,
			selectedSkillName: s.selectedSkillName,
			browserAllowed: s.browserAllowed,
			onSubmit: s.onSubmit,
			onCancel: s.onCancel,
			onSkillChange: s.onSkillChange,
			onBrowserAllowedChange: s.onBrowserAllowedChange,
		})),
	);

export const useChatInputDisabled = () => useChatStore((s) => s.isSending || s.sessionLoading || !s.activeSessionId);

export const useChatHeaderActions = () =>
	useChatStore(
		useShallow((s) => ({
			sessions: s.sessions,
			activeSessionId: s.activeSessionId,
			onSelectSession: s.onSelectSession,
			onCloseTab: s.onCloseTab,
			onRenameSession: s.onRenameSession,
			onNewSession: s.onNewSession,
		})),
	);

export const setChatState = (patch: Partial<Omit<ChatStoreState, "set">>) => useChatStore.getState().set(patch);
export const setQuote = (quote: AgentQuote | null) => setChatState({ quote });

export const useChatPendingJump = () => useChatStore((s) => s.pendingJump);
export const getPendingJump = () => useChatStore.getState().pendingJump;
export const requestMessageJump = (pendingJump: ChatMessageJump) => setChatState({ pendingJump });
export const clearMessageJump = () => setChatState({ pendingJump: null });

export default useChatStore;
